import { Stack, Typography } from '@mui/material';

import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { SidePanel } from '@/components/modals/SidePanel';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { GroupNodeMember } from '@/modules/estateWaterfall/types';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';

interface GroupDetailsDrawerProps {
  /** Household the presentation belongs to; used to deep-link into the app. */
  householdId: string;
  members: GroupNodeMember[];
  onClose: () => void;
}

interface GroupMemberRowProps {
  householdId: string;
  member: GroupNodeMember;
  /** Draw a divider above the row (every row after the first). */
  showTopBorder: boolean;
}

/**
 * A single member row. Openable entities are clickable and deep-link to the
 * entity detail page in a new tab (mirroring the tile's "Open in app" action);
 * non-entity members (sub-trusts, individuals, …) have no detail page, so they
 * list as plain, non-interactive rows.
 */
function GroupMemberRow({
  householdId,
  member,
  showTopBorder,
}: GroupMemberRowProps) {
  const openInNewTab = () => {
    const path = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
      { householdId, entityId: member.entityId }
    );
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={member.isOpenableEntity ? openInNewTab : undefined}
      sx={{
        p: 1.5,
        borderTop: showTopBorder ? `1px solid ${COLORS.GRAY[200]}` : undefined,
        cursor: member.isOpenableEntity ? 'pointer' : 'default',
        '&:hover': member.isOpenableEntity
          ? { backgroundColor: COLORS.FUNCTIONAL.HOVER }
          : undefined,
      }}
    >
      {/* 12px medium in primary text — no single variant is 12px + primary, so
          take `text-secondary`'s 12px shape and override the color. */}
      <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
        {member.displayName}
      </Typography>
      {member.isOpenableEntity && (
        <LinkExternal01Icon size={16} color={COLORS.GRAY[500]} />
      )}
    </Stack>
  );
}

/**
 * "Group details" drawer for an aggregate waterfall group tile: lists the
 * group's member entities and opens each in a new tab. Read-only, so it stays
 * available to view-only collaborators. Mounted by
 * {@link PresentationEntityActionsProvider} outside the diagram's remount-keyed
 * subtree so an in-place remeasure can't tear it down.
 */
export function GroupDetailsDrawer({
  householdId,
  members,
  onClose,
}: GroupDetailsDrawerProps) {
  const itemLabel = members.length === 1 ? 'item' : 'items';

  return (
    <SideDrawer
      isOpen
      onClose={onClose}
      width={OVERLAY_MD}
      heading="Group details"
      data-testid="GroupDetailsDrawer"
      // The body's 16px padding + 16px gap come from SidePanel.Content, so this
      // drops the SideDrawer body's own padding to avoid doubling it.
      bodyPropsSx={{ p: 0 }}
      body={
        <SidePanel.Content sx={{ flexDirection: 'column', width: '100%' }}>
          <Typography variant="body-s">
            {`This group contains ${members.length} ${itemLabel}, select a row to open in new tab.`}
          </Typography>
          <Stack
            sx={{
              width: '100%',
              borderBottom: `1px solid ${COLORS.GRAY[200]}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ p: 1.5, backgroundColor: COLORS.GRAY[50] }}
            >
              <Typography variant="body-xs">Entity name</Typography>
            </Stack>
            {members.map((member, index) => (
              <GroupMemberRow
                key={member.entityId}
                householdId={householdId}
                member={member}
                showTopBorder={index > 0}
              />
            ))}
          </Stack>
        </SidePanel.Content>
      }
    />
  );
}
